import React from "react";

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    const date = new Date(value);
    const datePart = new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" }).format(date);
    const timePart = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: true })
      .format(date).replace(":", ".").replace(/\s+/g, " ").trim();
    return `${datePart} ${timePart}`;
  } catch { return String(value); }
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  try { return new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value)); }
  catch { return String(value); }
};

const toText = (value, fallback = "-") => { const t = String(value ?? "").trim(); return t || fallback; };

export function AllTasksReportPdfDocument({ components, logoUrl, companyInfo, filters, report, selectedColumns, generatedAt }) {
  const { Document, Page, StyleSheet, Text, View, Image } = components;
  const activeColumns = Array.isArray(selectedColumns) && selectedColumns.length
    ? selectedColumns
    : ["department", "employee", "task", "description", "createdBy", "created", "due", "priority", "status", "activity", "actions"];
  const serialColumnWidth = 22;
  const columnWidth = { flex: 1 };

  const styles = StyleSheet.create({
    page: {
      paddingTop: 28,
      paddingBottom: 28,
      paddingHorizontal: 30,
      backgroundColor: "#ffffff",
      fontFamily: "HindSiliguri",
      fontSize: 8.6,
      color: "#0f172a",
    },

    /* ── Header ── */
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingBottom: 10,
      borderBottomWidth: 1.5,
      borderBottomColor: "#0f172a",
      marginBottom: 12,
    },
    brandRow: { flexDirection: "column", alignItems: "flex-start" },
    logo: { width: 100, objectFit: "contain", marginBottom: 2 },
    companyName: { fontSize: 15, fontWeight: 700, color: "#0f172a", letterSpacing: -0.2 },
    companyTagline: { fontSize: 8, color: "#64748b", marginTop: 1, letterSpacing: 0.3 },
    contactBlock: { alignItems: "flex-start" },
    contactRow: { flexDirection: "row", alignItems: "center", marginBottom: 3 },
    contactLabel: { fontSize: 8, fontWeight: 700, color: "#0f172a", width: 52 },
    contactLine: { fontSize: 8, color: "#475569" },

    /* ── Meta bar ── */
    metaBar: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#f8fafc",
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 6,
      paddingVertical: 7,
      paddingHorizontal: 11,
      marginBottom: 14,
    },
    metaLeft: { flexDirection: "column" },
    metaReportName: { fontSize: 11, fontWeight: 700, color: "#0f172a", marginBottom: 1 },
    metaFilter: { fontSize: 7.8, color: "#475569", lineHeight: 1.55 },
    metaRight: { alignItems: "flex-end" },
    metaGenLabel: { fontSize: 7, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 1 },
    metaGenValue: { fontSize: 8, color: "#334155" },

    /* ── Table ── */
    table: {
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 7,
      overflow: "hidden",
    },
    tHead: {
      flexDirection: "row",
      borderBottomWidth: 1.5,
      borderBottomColor: "#0f172a",
      paddingVertical: 7,
      paddingHorizontal: 4,
    },
    tHeadCell: {
      fontSize: 7.2,
      fontWeight: 700,
      color: "#0f172a",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      paddingHorizontal: 6,
    },
    tRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#f1f5f9",
      paddingVertical: 6,
      paddingHorizontal: 4,
    },
    tRowAlt: { backgroundColor: "#f8fafc" },
    tRowLast: { borderBottomWidth: 0 },
    cell: { paddingHorizontal: 6 },
    cellStrong: { fontSize: 7.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.4 },
    cellText: { fontSize: 7.1, color: "#475569", lineHeight: 1.35, marginTop: 1 },
    serialHeadCell: {
      fontSize: 7.2,
      fontWeight: 700,
      color: "#0f172a",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      paddingHorizontal: 6,
      textAlign: "center",
    },
    serialCell: {
      paddingHorizontal: 6,
      fontSize: 7.5,
      fontWeight: 700,
      color: "#0f172a",
      textAlign: "center",
    },

    /* ── Empty / Footer ── */
    emptyBox: {
      borderWidth: 1, borderColor: "#e2e8f0", borderRadius: 7,
      paddingVertical: 20, alignItems: "center",
    },
    emptyText: { fontSize: 9, color: "#94a3b8" },
    footer: {
      marginTop: 10, paddingTop: 8,
      borderTopWidth: 1, borderTopColor: "#e2e8f0",
      flexDirection: "row", justifyContent: "space-between", alignItems: "center",
    },
    footerText: { fontSize: 7.6, color: "#94a3b8" },
  });

  const statusCounts = Array.isArray(report?.statusCounts) ? report.statusCounts : [];
  const priorityCounts = Array.isArray(report?.priorityCounts) ? report.priorityCounts : [];
  const completedCount = statusCounts.find((item) => item.status === "Completed")?.count || 0;
  const inProgressCount = statusCounts.find((item) => item.status === "In Progress")?.count || 0;
  const urgentCount = priorityCounts.find((item) => item.priority === "Urgent")?.count || 0;
  const totalItems = report?.items?.length || 0;

  const filterParts = [
    filters?.departmentName && filters.departmentName !== "All Departments" ? `Dept: ${filters.departmentName}` : null,
    filters?.employeeName && filters.employeeName !== "All Employees" ? `Employee: ${filters.employeeName}` : null,
    (filters?.startDate || filters?.endDate) ? `Date: ${formatDateOnly(filters?.startDate)} to ${formatDateOnly(filters?.endDate)}` : null,
    filters?.status && filters.status !== "All Statuses" ? `Status: ${filters.status}` : null,
    filters?.priority && filters.priority !== "All Priorities" ? `Priority: ${filters.priority}` : null,
  ].filter(Boolean);

  const columns = [
    {
      key: "department",
      header: "Department",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{toText(item.departmentName, "No Dept")}</Text>
        </View>
      ),
    },
    {
      key: "employee",
      header: "Employee",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{toText(item.assignedEmployeeName, "Unassigned")}</Text>
          <Text style={styles.cellText}>{toText(item.assignedEmployeeEmail || item.assignedUserId, "No email")}</Text>
        </View>
      ),
    },
    {
      key: "task",
      header: "Title",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{toText(item.title, "Untitled task")}</Text>
        </View>
      ),
    },
    {
      key: "description",
      header: "Description",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellText}>{toText(item.description, "No description")}</Text>
        </View>
      ),
    },
    {
      key: "createdBy",
      header: "Created By",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellText}>{toText(item.createdByName, "Unknown")}</Text>
        </View>
      ),
    },
    {
      key: "created",
      header: "Created",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellText}>{formatDateOnly(item.createdAt)}</Text>
        </View>
      ),
    },
    {
      key: "due",
      header: "Due",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellText}>{formatDateOnly(item.dueDate)}</Text>
        </View>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{toText(item.priority, "Low")}</Text>
        </View>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{toText(item.status, "Pending")}</Text>
          <Text style={styles.cellText}>Progress {Number(item.progressPercent || 0)}%</Text>
        </View>
      ),
    },
    {
      key: "activity",
      header: "Last Activity",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{formatDateTime(item.lastActivityAt || item.updatedAt)}</Text>
          <Text style={styles.cellText}>{toText(item.lastActionLabel, "Task Updated")} by {toText(item.lastActorName, "Unknown")}</Text>
          <Text style={styles.cellText}>Due {formatDateOnly(item.dueDate)}</Text>
        </View>
      ),
    },
    {
      key: "actions",
      header: "Source",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{item.ticketId ? "Ticket / Task" : "Task Desk"}</Text>
          <Text style={styles.cellText}>{item.ticketId ? `Ticket ${toText(item.ticketNumber, "Open")}` : "Manual task"}</Text>
        </View>
      ),
    },
  ].filter((column) => activeColumns.includes(column.key));

  return (
    <Document title="All Tasks Report" author="BanglaFire CRM" subject="Tasks report" creator="BanglaFire CRM" producer="BanglaFire CRM">
      <Page size="A4" orientation="landscape" style={styles.page}>

        {/* ── HEADER ── */}
        <View style={styles.header}>
          <View style={styles.brandRow}>
            {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : null}
            <Text style={styles.companyName}>{toText(companyInfo?.name, "BanglaFire CRM")}</Text>
            <Text style={styles.companyTagline}>Customer Relationship Management</Text>
          </View>
          <View style={styles.contactBlock}>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Address :</Text>
              <Text style={styles.contactLine}>39/9 A, Senpara Parbata, Mirpur, Dhaka-1216</Text>
            </View>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Mobile :</Text>
              <Text style={styles.contactLine}>(+880) 9639222999  |  1709927934</Text>
            </View>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Email :</Text>
              <Text style={styles.contactLine}>md@banglafire.com  |  support@banglafire.com</Text>
            </View>
          </View>
        </View>

        {/* ── META BAR ── */}
        <View style={styles.metaBar}>
          <View style={styles.metaLeft}>
            <Text style={styles.metaReportName}>All Tasks Report</Text>
            {filterParts.length > 0 && (
              <Text style={styles.metaFilter}>{filterParts.join("   •   ")}</Text>
            )}
          </View>
          <View style={styles.metaRight}>
            <Text style={styles.metaGenLabel}>Generated</Text>
            <Text style={styles.metaGenValue}>{formatDateTime(generatedAt)}</Text>
            <Text style={[styles.metaGenLabel, { marginTop: 4 }]}>Total Records</Text>
            <Text style={[styles.metaGenValue, { fontWeight: 700, color: "#0f172a" }]}>{totalItems}</Text>
            <Text style={[styles.metaGenLabel, { marginTop: 4 }]}>Completed / Urgent</Text>
            <Text style={[styles.metaGenValue]}>{completedCount} / {urgentCount}  (In Progress: {inProgressCount})</Text>
          </View>
        </View>

        {/* ── TABLE ── */}
        {totalItems > 0 ? (
          <View style={styles.table}>
            <View style={styles.tHead}>
              <Text style={[styles.serialHeadCell, { width: serialColumnWidth }]}>#</Text>
              {columns.map((column) => (
                <Text key={column.key} style={[styles.tHeadCell, columnWidth]}>
                  {column.header}
                </Text>
              ))}
            </View>
            {report.items.map((item, index) => (
              <View
                key={item.taskId || `${item.title}-${index}`}
                style={[styles.tRow, index % 2 === 1 ? styles.tRowAlt : null, index === report.items.length - 1 ? styles.tRowLast : null]}
                wrap={false}
              >
                <Text style={[styles.serialCell, { width: serialColumnWidth }]}>{index + 1}</Text>
                {columns.map((column) => (
                  <React.Fragment key={column.key}>
                    {column.render(item)}
                  </React.Fragment>
                ))}
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.emptyBox}>
            <Text style={styles.emptyText}>No tasks matched the selected filters.</Text>
          </View>
        )}

        {/* ── FOOTER ── */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Generated on {formatDateTime(generatedAt)}</Text>
          <Text style={styles.footerText}>BanglaFire CRM  |  Confidential</Text>
        </View>
      </Page>
    </Document>
  );
}
